import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Loader2, CalendarDays, CheckCircle2, Sparkles, Mic, Square, Volume2, VolumeX } from "lucide-react";
import { conciergeInfo, conciergeChat, conciergeBook, conciergeVoice, conciergeSpeak } from "@/lib/api";

const DEMO = "ava";

// Inline, on-page "try it now" version of Ava — reuses the tested concierge
// chat + booking endpoints (creates a real CRM lead) and shows a realistic
// "appointment booked" confirmation (no real calendar is touched).
export default function AvaLiveDemo() {
  const [prompts, setPrompts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("chat"); // chat | book | done
  const [form, setForm] = useState({ name: "", contact: "", date: "", time: "", notes: "" });
  const [bookBusy, setBookBusy] = useState(false);
  const [bookErr, setBookErr] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [voiceOn, setVoiceOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const sessionRef = useRef(`ava-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    conciergeInfo(DEMO).then((d) => {
      setPrompts(d.prompts || []);
      setMessages([{ role: "assistant", content: d.greeting || "Hi, I'm Ava." }]);
    }).catch(() => setMessages([{ role: "assistant", content: "Hi, I'm Ava — WebsiteTech's AI receptionist." }]));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: q }]);
    setInput("");
    setBusy(true);
    try {
      const res = await conciergeChat(DEMO, q, history, sessionRef.current);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      if (voiceOn) speak(res.reply);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I had trouble responding. Please try again." }]);
    } finally { setBusy(false); }
  };

  const speak = async (text) => {
    try {
      const { audio_base64, mime } = await conciergeSpeak(text, DEMO);
      if (audioRef.current) audioRef.current.pause();
      const el = new Audio(`data:${mime};base64,${audio_base64}`);
      audioRef.current = el;
      el.play().catch(() => {});
    } catch { /* spoken reply is best-effort */ }
  };

  const startRecording = async () => {
    if (recording || busy || voiceBusy) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 800) { setVoiceBusy(false); return; }
        setVoiceBusy(true);
        const history = messages.map((m) => ({ role: m.role, content: m.content }));
        const fd = new FormData();
        fd.append("demo", DEMO);
        fd.append("session_id", sessionRef.current);
        fd.append("history", JSON.stringify(history));
        fd.append("audio", blob, "clip.webm");
        try {
          const res = await conciergeVoice(fd);
          const transcript = (res.transcript || "").trim();
          if (transcript.length < 2) {
            setMessages((m) => [...m, { role: "assistant", content: "I couldn't quite catch that — please try again or type your message." }]);
          } else {
            setMessages((m) => [...m, { role: "user", content: transcript }, { role: "assistant", content: res.reply }]);
            if (voiceOn) speak(res.reply);
          }
        } catch {
          setMessages((m) => [...m, { role: "assistant", content: "Sorry — I couldn't catch that. Please try again or type." }]);
        } finally { setVoiceBusy(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I couldn't access your microphone. Please allow mic access or type your message." }]);
    }
  };
  const stopRecording = () => { if (recorderRef.current && recording) { recorderRef.current.stop(); setRecording(false); } };

  const submitBooking = async () => {
    setBookErr("");
    const nm = form.name.trim(), contact = form.contact.trim();
    if (!nm || !contact || !form.date || !form.time) { setBookErr("Please fill in your name, contact, date and time."); return; }
    const isEmail = contact.includes("@");
    setBookBusy(true);
    try {
      const res = await conciergeBook({
        demo: DEMO, session_id: sessionRef.current, name: nm,
        email: isEmail ? contact : null, phone: isEmail ? null : contact,
        date: form.date, time: form.time, notes: form.notes.trim() || null,
      });
      setConfirmed({ name: nm.split(" ")[0], date: form.date, time: form.time, msg: res.confirmation });
      setView("done");
    } catch (e) {
      setBookErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBookBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white soft-shadow overflow-hidden max-w-md w-full mx-auto" data-testid="ava-live-demo">
      {/* header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white">
        <div className="flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-brand font-bold">Av</div>
          <div>
            <div className="text-sm font-semibold leading-tight flex items-center gap-1.5">Ava <span className="text-[9px] font-bold uppercase bg-emerald-500 rounded-full px-1.5 py-0.5">Live demo</span></div>
            <div className="text-[11px] text-slate-400 leading-tight">WebsiteTech AI receptionist</div>
          </div>
        </div>
        {view === "chat" && (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setVoiceOn((v) => !v)} data-testid="ava-voice-toggle" title={voiceOn ? "Spoken replies on" : "Spoken replies off"}
              className={`grid h-7 w-7 place-items-center rounded-full transition-colors ${voiceOn ? "bg-brand" : "bg-white/10 hover:bg-white/20"}`}>
              {voiceOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            </button>
            <button onClick={() => setView("book")} data-testid="ava-book-btn"
              className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-brand hover:bg-blue-600 transition-colors">
              <CalendarDays size={13} /> Book
            </button>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {view === "done" ? (
          <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 text-center" data-testid="ava-booked-confirmation">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center text-emerald-600"><CheckCircle2 size={30} /></div>
            <h4 className="font-heading text-lg font-bold text-slate-900 mt-3">Appointment booked</h4>
            <p className="text-sm text-slate-500 mt-1">{confirmed?.name}, you're set for <strong className="text-slate-700">{confirmed?.date} at {confirmed?.time}</strong>.</p>
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3 text-xs text-slate-500 text-left">{confirmed?.msg}</div>
            <div className="mt-3 text-[11px] text-emerald-600 font-semibold">✓ Captured to the CRM as a real lead</div>
            <button onClick={() => { setView("chat"); setConfirmed(null); setForm({ name: "", contact: "", date: "", time: "", notes: "" }); }}
              className="mt-4 text-sm font-semibold text-brand hover:underline" data-testid="ava-restart">Try again</button>
          </motion.div>
        ) : view === "book" ? (
          <motion.div key="book" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 space-y-2.5" data-testid="ava-book-form">
            <div className="text-sm font-semibold text-slate-800">Book a free consultation</div>
            <div className="text-[11px] text-slate-400 -mt-1">Share your details and the team will confirm.</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="ava-book-name"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or phone" data-testid="ava-book-contact"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
            <div className="flex gap-2">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="ava-book-date"
                className="w-1/2 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="ava-book-time"
                className="w-1/2 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
            </div>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we should know? (optional)" rows={2} data-testid="ava-book-notes"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand resize-none" />
            {bookErr && <div className="text-[11px] text-rose-500" data-testid="ava-book-error">{bookErr}</div>}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setView("chat")} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">Back to chat</button>
              <button onClick={submitBooking} disabled={bookBusy} data-testid="ava-book-submit"
                className="flex-1 rounded-lg bg-brand text-white px-3 py-2 text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {bookBusy ? <Loader2 size={14} className="animate-spin" /> : null} Book it
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div ref={scrollRef} className="h-[320px] overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-brand text-white rounded-br-sm" : "bg-slate-100 text-slate-700 rounded-bl-sm"}`}>{m.content}</div>
                </div>
              ))}
              {busy && <div className="flex justify-start"><div className="rounded-2xl bg-slate-100 px-3.5 py-2"><Loader2 size={15} className="animate-spin text-brand" /></div></div>}
              {messages.length <= 1 && prompts.length > 0 && (
                <div className="pt-1 space-y-2">
                  {prompts.map((p, pi) => (
                    <button key={p} onClick={() => send(p)} data-testid={`ava-prompt-${pi}`}
                      className="block w-full text-left text-xs rounded-lg border border-slate-200 px-3 py-2 text-slate-600 hover:border-brand hover:text-brand transition-colors">{p}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 border-t border-slate-100">
              <div className="flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5">
                <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask Ava anything…" data-testid="ava-chat-input"
                  className="flex-1 bg-transparent text-sm outline-none text-slate-700 placeholder:text-slate-400" />
                <button onClick={recording ? stopRecording : startRecording} disabled={busy || voiceBusy}
                  data-testid="ava-mic-btn" title={recording ? "Stop recording" : "Tap to talk"}
                  className={`grid h-8 w-8 place-items-center rounded-full disabled:opacity-50 transition-colors ${recording ? "bg-rose-500 text-white" : "bg-slate-200 text-slate-600 hover:bg-slate-300"}`}>
                  {voiceBusy ? <Loader2 size={15} className="animate-spin" /> : recording ? <Square size={13} /> : <Mic size={15} />}
                </button>
                <button onClick={() => send()} disabled={busy} data-testid="ava-send-btn"
                  className="grid h-8 w-8 place-items-center rounded-full bg-brand text-white disabled:opacity-50"><Send size={15} /></button>
              </div>
              <div className="mt-2 text-center text-[10px] text-slate-400 flex items-center justify-center gap-1"><Sparkles size={10} /> {recording ? "Listening… tap the square to send" : voiceBusy ? "Transcribing…" : "Real AI · type or tap the mic to talk"}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
